/**
 * enums.ts
 *
 * Canonical enum-like Zod schemas for values that are constrained to a
 * fixed set of strings across the whole system (roles, content lifecycle
 * status, content types, publish targets, audit action names).
 *
 * These are the ONLY place these value sets are defined. The D1 schema
 * (apps/worker/src/db/schema.ts) uses the same literal strings in its
 * `text({ enum: [...] })` column definitions — if you add a value here,
 * add it there too, and add a migration.
 */
import { z } from "zod";

/**
 * The three roles in the system, in ascending order of power. `manager`'s
 * capabilities are fixed and cannot be revoked (see the fail-safe default
 * in `requireCapability`, apps/worker/src/lib/auth.ts, which grants a
 * manager every capability unconditionally rather than trusting the
 * editable permission matrix). `editor` and `publisher` are both
 * configurable via that matrix: `publisher` ships by default with
 * everything `editor` has PLUS publish/schedule/taxonomy/variable
 * management, but a manager can reconfigure either role's exact
 * capability set from Admin → Roles & Permissions without a code
 * change (see `capabilitySchema` below and `role_permissions` in
 * db/schema.ts) — this is what makes the RBAC "granular" rather than a
 * fixed two-tier switch.
 */
export const roleSchema = z.enum(["editor", "publisher", "manager"]);
export type Role = z.infer<typeof roleSchema>;

/**
 * The fixed set of capabilities the permission matrix grants per role.
 * Each is checked by `requireCapability(capability)`
 * (apps/worker/src/lib/auth.ts) at the specific routes named below — this
 * list and that set of call sites must be kept in sync. A handful of
 * especially sensitive actions (user management, the permission matrix
 * itself, viewing the audit log, promoting staging to production) are
 * deliberately NOT in this list and stay hardcoded manager-only
 * (`requireRole("manager")`) instead — putting them behind an editable
 * matrix would let a manager accidentally lock themselves out of the one
 * screen that could undo the mistake.
 */
export const capabilitySchema = z.enum([
  /** content.ts: POST /:id/publish, /:id/schedule, /:id/unschedule, /:id/reject */
  "content.publish",
  /** content.ts: POST /:id/archive */
  "content.archive",
  /** categories.ts, authors.ts, tags.ts: POST/PATCH/DELETE */
  "taxonomy.manage",
  /** variables.ts: POST/PATCH/DELETE */
  "variables.manage",
  /** redirects.ts: POST/DELETE */
  "redirects.manage",
  /** sites.ts: POST/PATCH/DELETE */
  "sites.manage",
  /** ai.ts: ask the AI for topic ideas or drafts (costs money per call). Approving what it produces still needs content.publish. */
  "ai.use",
  /** evidence.ts routes: add or retire evidence records by hand (the AI never does). */
  "evidence.manage",
]);
export type Capability = z.infer<typeof capabilitySchema>;

/**
 * Content lifecycle status. Linear happy path:
 *   draft -> pending_review -> scheduled -> published -> archived
 * `pending_review` can also go straight to `published` (immediate publish,
 * skipping schedule) or back to `draft` (rejected in review).
 * Only a manager may move content into scheduled/published/archived
 * (enforced server-side in apps/worker/src/routes/content.ts).
 */
export const contentStatusSchema = z.enum([
  "draft",
  "pending_review",
  "scheduled",
  "published",
  "archived",
  /** v2: a scheduled item whose automatic publish failed MAX_PUBLISH_ATTEMPTS
   * times (see publishEngine.ts). It stays visible and recoverable instead of
   * retrying forever. */
  "failed",
]);
export type ContentStatus = z.infer<typeof contentStatusSchema>;

/**
 * The three content types the CMS manages. Product/landing pages are out
 * of scope. Policy pages were dropped (see docs/ARCHITECTURE.md): the real
 * DemandHub site renders /privacy/ and /tou/ from hand-authored .astro
 * files with no markdown/content-collection mechanism at all, so there was
 * nothing for a CMS-published file to plug into without also changing the
 * Astro site's own code — out of scope for this round, by client decision.
 */
export const contentTypeSchema = z.enum(["article", "case_study", "ebook"]);
export type ContentType = z.infer<typeof contentTypeSchema>;

/**
 * Where a publish/schedule action commits to. "staging" commits to the
 * site's production_branch is NOT what this means — see sites table:
 * "staging" -> site.staging_branch, "production" -> site.production_branch.
 * Any other branch (ad hoc preview) is out of scope for this build; the
 * data model leaves room for it via content_versions.preview_branch.
 */
export const publishTargetSchema = z.enum(["staging", "production"]);
export type PublishTarget = z.infer<typeof publishTargetSchema>;

/**
 * Every mutating action in the system writes exactly one of these to the
 * audit log via apps/worker/src/lib/audit.ts#logAudit(). Keep this list in
 * sync with every call site of logAudit.
 */
export const auditActionSchema = z.enum([
  "created",
  "edited",
  "submitted",
  "approved",
  "published",
  "publish_failed",
  "scheduled",
  "unscheduled",
  "archived",
  "rolled_back",
  "rejected",
  "slug_changed",
  "image_committed",
  "image_staged",
  "promoted_to_production",
  "user_invited",
  "role_changed",
  "user_sites_changed",
  "user_deactivated",
  "user_activated",
  "user_deleted",
  "user_invite_resent",
  "password_changed",
  "password_reset",
  "mfa_enabled",
  "mfa_disabled",
  "mfa_reset_by_manager",
  "session_revoked",
  "sign_in",
  "tag_created",
  "tag_deleted",
  "redirect_created",
  "redirect_deleted",
  "site_settings_updated",
  "category_created",
  "category_updated",
  "category_deleted",
  "author_created",
  "author_updated",
  "author_deleted",
  "variable_created",
  "variable_updated",
  "variable_deleted",
  "permission_updated",
  "imported_from_git",
  // v2 foundation: publish engine, approvals, AI layer, handoff, memory.
  "publish_blocked",
  "approval_recorded",
  "ai_proposal_created",
  "ai_proposal_approved",
  "ai_proposal_rejected",
  "handoff_takeover",
  "handoff_handback",
  "handoff_paused",
  "memory_rule_proposed",
  "memory_rule_approved",
  "memory_rule_rejected",
  "memory_rule_deleted",
  // Calendar and editor additions.
  "deleted",
  "planned",
  "duplicated",
  "ai_assist_used",
  // Admin → API keys: the key is named, never its value.
  "api_key_saved",
  "api_key_removed",
  // Calendar holidays and events.
  "calendar_event_saved",
  "calendar_event_removed",
  "calendar_events_imported",
  "calendar_feed_added",
  "calendar_feed_removed",
  "calendar_feed_synced",
  // Evidence records.
  "evidence_added",
  "evidence_retired",
  "keywords_refreshed",
  // A manager set what a model costs per million tokens.
  "ai_price_set",
  // "Check the web" (Copyscape) was run on an AI draft.
  "web_check_run",
  // AI suggested social post wording for an article (nothing saved).
  "social_ai_drafted",
  // A manager started a quality-check run against the live AI.
  "quality_run_started",
  // Social posts (written here, approved by a second person). The two older "synced" and "scheduled" entries stay so past history still reads.
  "social_channel_added",
  "social_channel_removed",
  "social_channels_synced",
  "social_post_created",
  "social_post_edited",
  "social_post_submitted",
  "social_post_approved",
  "social_post_changes_requested",
  "social_post_scheduled",
  "social_channel_updated",
  "social_rule_switched",
  "social_agents_run",
  "social_post_commented",
  "social_suggestion_answered",
  "social_post_cancelled",
  "social_post_sent",
  "sociamonials_settings_changed",
  "sociamonials_profiles_synced",
  // Brain: brand profile and knowledge library.
  "brand_profile_saved",
  "knowledge_added",
  "knowledge_retired",
  // Ads research: watchlist, competitor ads, own ad results.
  "ads_watchlist_added",
  "ads_watchlist_edited",
  "ads_watchlist_retired",
  "ads_watchlist_restored",
  "ads_competitor_ad_added",
  "ads_competitor_ad_edited",
  "ads_competitor_ad_retired",
  "ads_competitor_ads_imported",
  "ads_competitor_ads_fetched",
  "ads_results_added",
  "ads_results_imported",
  "ads_batch_retired",
  "ads_google_connected",
  "ads_brand_kit_saved",
  "ads_rule_switched",
  "ads_google_disconnected",
  "ads_alert_answered",
  "ads_google_results_received",
  "ads_linkedin_results_received",
  "ads_sample_loaded",
  "ads_sample_removed",
  "ads_name_added",
  "ads_name_approved",
  "ads_name_retired",
  // Ad drafts: generated, edited, reviewed, regenerated.
  "ad_draft_created",
  "ad_draft_edited",
  "ad_draft_reviewed",
  "ads_concept_reviewed",
  "ad_draft_regenerated",
  "ad_reviewers_rerun",
  "ad_scene_made",
  "ad_rule_verified",
  "ad_reference_set",
  "ad_rule_rejected",
  "ad_draft_retired",
  "ad_export_google_editor",
  "ad_semrush_lookup",
  "ad_semrush_advertisers",
  "ad_schedule_changed",
  "prompt_saved",
  "social_ideas_generated",
  "social_posts_generated",
  "social_scene_made",
  "social_reviewers_rerun",
  "social_rule_verified",
  "social_rule_rejected",
  "social_reference_set",
  "social_idea_dismissed",
  "git_branch_created",
]);
export type AuditAction = z.infer<typeof auditActionSchema>;

/** The two audit target kinds — what `target_id` on an audit_log row refers to. */
export const auditTargetTypeSchema = z.enum([
  "content",
  "user",
  "site",
  "tag",
  "redirect",
  "category",
  "author",
  "variable",
  "role_permission",
  "proposal",
  "memory_rule",
  "api_key",
  "calendar_event",
  "calendar_feed",
  "evidence",
  "brand_profile",
  "knowledge_doc",
  "social_post",
  "quality_run",
  "ads",
]);
export type AuditTargetType = z.infer<typeof auditTargetTypeSchema>;
